// EEN datumnotatie per soort oppervlak (eigenaarsbesluit 18 oktober 2026).
//
// Het formaat hangt af van het OPPERVLAK, niet van het bestand waarin je zit. Elke
// datum of tijd die een mens ziet loopt door fmtDate(value, kind). Altijd nl-NL
// (of en-GB), altijd Europe/Amsterdam, altijd 24 uur, nooit een punt achter een
// afkorting, alleen komma of spatie als scheiding (Regel 32: geen middot, geen
// gedachtestreepje). "Jaar" betekent overal: alleen als het afwijkt van het huidige
// Amsterdamse jaar, behalve bij audit.
//
//   cell    di 6 okt | vr 11 sep | do 21 mrt 2024          gridcel, kaartveld, geen tijd
//   feed    13 min | 09:05 | gisteren 17:44 | 6 okt 14:02 | 6 okt 2024
//   agenda  14 min | morgen 10:00 | vr 6 nov | ma 4 jan 2027 10:30
//   header  maandag 19 oktober | maandag 4 januari 2027
//   notice  ma 19 okt, 10:00 | do 4 dec 2025, 11:00       opgeslagen tekst, nooit relatief
//   axis    okt | jan 2026                                grafiekas
//   audit   zo 18 okt 2026, 19:16                         logs en de hover van elke andere soort
//
// Invoer: Date, epoch-milliseconden, ISO-tijdstip, 'YYYY-MM-DD' (een kalenderdag,
// nooit verschoven door een tijdzone, en dus zonder tijd), 'YYYY-MM' (een maand),
// of 'YYYY-MM-DD HH:MM[:SS]' / 'YYYY-MM-DDTHH:MM' zonder zone (Amsterdamse
// wandkloktijd, zoals voip_calls.called_at).
// Onleesbare invoer wordt '-'.
//
// Exports (CSV/XLSX) en datuminvoervelden vallen hier buiten: die zijn machinetaal.

export const DATE_KINDS = ['cell', 'feed', 'agenda', 'header', 'notice', 'axis', 'audit'];
export const TZ = 'Europe/Amsterdam';

const L = {
  nl: {
    wdShort: ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'],
    wdLong: ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'],
    monShort: ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
    monLong: ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'],
    now: 'nu', min: 'min', today: 'vandaag', yesterday: 'gisteren', tomorrow: 'morgen', until: 't/m',
  },
  en: {
    wdShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    wdLong: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    monShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    monLong: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    now: 'now', min: 'min', today: 'today', yesterday: 'yesterday', tomorrow: 'tomorrow', until: 'to',
  },
};

const PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
});

const RE_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const RE_MONTH = /^(\d{4})-(\d{2})$/;
// Zonder zone-aanduiding: Amsterdamse wandklok, ook met een T ertussen.
const RE_WALL = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

// Weekdag van een kalenderdag, zonder tijdzone (UTC-middag van die dag).
function weekdayOf(y, m, d) { return new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay(); }

function amsParts(ms) {
  const p = {};
  for (const { type, value } of PARTS.formatToParts(new Date(ms))) p[type] = value;
  const y = +p.year, m = +p.month, d = +p.day;
  return { y, m, d, hh: p.hour === '24' ? '00' : p.hour, mi: p.minute, wd: weekdayOf(y, m, d), hasTime: true, ms };
}

// Leest een waarde naar Amsterdamse delen, of null. `hasTime` is false voor een
// kalenderdag, `monthOnly` voor 'YYYY-MM'.
export function parseMoment(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : amsParts(value.getTime());
  if (typeof value === 'number') return Number.isFinite(value) && !Number.isNaN(new Date(value).getTime()) ? amsParts(value) : null;
  const s = String(value).trim();
  let m = RE_DAY.exec(s);
  if (m) {
    const y = +m[1], mo = +m[2], d = +m[3];
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    // Onmogelijke kalenderdag (31 feb): rondreis via UTC geeft '-'. Zelfde controle in de wandklokvorm.
    // Een ISO-instant met Z of offset ('2026-02-31T10:00:00Z') valt hier niet onder: Date.parse
    // rolt die door naar 3 mrt, want een instant is een moment en geen kalenderdag.
    if (new Date(Date.UTC(y, mo - 1, d)).getUTCDate() !== d) return null;
    return { y, m: mo, d, hh: null, mi: null, wd: weekdayOf(y, mo, d), hasTime: false, ms: Date.UTC(y, mo - 1, d, 12) };
  }
  m = RE_MONTH.exec(s);
  if (m) {
    const y = +m[1], mo = +m[2];
    if (mo < 1 || mo > 12) return null;
    return { y, m: mo, d: 1, hh: null, mi: null, wd: weekdayOf(y, mo, 1), hasTime: false, monthOnly: true, ms: Date.UTC(y, mo - 1, 1, 12) };
  }
  m = RE_WALL.exec(s);
  if (m) {
    const y = +m[1], mo = +m[2], d = +m[3];
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    if (new Date(Date.UTC(y, mo - 1, d)).getUTCDate() !== d) return null;
    // Wandklok: de delen zijn al Amsterdams. ms benaderen via de offset van dat moment.
    const guess = Date.UTC(y, mo - 1, d, +m[4], +m[5]);
    const off = amsOffsetMs(guess);
    return { y, m: mo, d, hh: m[4], mi: m[5], wd: weekdayOf(y, mo, d), hasTime: true, ms: guess - off };
  }
  const t = Date.parse(s);
  if (Number.isNaN(t)) return null;
  return amsParts(t);
}

function amsOffsetMs(utcMs) {
  const p = amsParts(utcMs);
  return Date.UTC(p.y, p.m - 1, p.d, +p.hh, +p.mi) - Math.floor(utcMs / 60000) * 60000;
}

const pad = (n) => String(n).padStart(2, '0');
const keyOf = (p) => `${p.y}-${pad(p.m)}-${pad(p.d)}`;
function dayDiff(a, b) {
  // Kalenderdagen van a tot b (b min a), los van tijdzone en zomertijd.
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86400000);
}

// 'YYYY-MM-DD' in Europe/Amsterdam: de enige definitie van "welke dag is dit".
export function amsterdamDayKey(value = new Date()) {
  const p = parseMoment(value);
  return p ? keyOf(p) : '';
}

function langOf(lang) { return lang === 'en' ? L.en : L.nl; }

// Een datum of tijd voor een mens. kind: zie de tabel bovenaan.
// opts: now (Date, voor relatief en "ander jaar"), lang ('nl' | 'en'),
//       time (false onderdrukt de tijd bij agenda, notice en audit, bijvoorbeeld een
//       hele-dagafspraak), unit ('day' voor dag-ticks op een as).
export function fmtDate(value, kind = 'cell', opts = {}) {
  const p = parseMoment(value);
  if (!p) return '-';
  const T = langOf(opts.lang);
  const now = opts.now instanceof Date ? opts.now : (opts.now != null ? new Date(opts.now) : new Date());
  const n = amsParts(now.getTime());
  const otherYear = p.y !== n.y;
  const yr = otherYear ? ` ${p.y}` : '';
  const showTime = p.hasTime && opts.time !== false;
  const clock = showTime ? `${p.hh}:${p.mi}` : '';
  const dm = `${p.d} ${T.monShort[p.m - 1]}`;
  const wdm = `${T.wdShort[p.wd]} ${dm}`;
  const diffMin = p.hasTime ? (n.ms - p.ms) / 60000 : null;
  const days = dayDiff(n, p);

  switch (kind) {
    case 'cell':
      return `${wdm}${yr}`;
    case 'feed': {
      if (p.hasTime && diffMin > -1 && diffMin < 60) {
        return diffMin < 1 ? T.now : `${Math.floor(diffMin)} ${T.min}`;
      }
      if (days === 0) return p.hasTime ? clock : T.today;
      if (days === -1) return p.hasTime ? `${T.yesterday} ${clock}` : T.yesterday;
      if (otherYear) return `${dm} ${p.y}`;
      return p.hasTime ? `${dm} ${clock}` : dm;
    }
    case 'agenda': {
      if (showTime && diffMin <= 0 && diffMin > -60) {
        const ahead = -diffMin;
        return ahead < 1 ? T.now : `${Math.ceil(ahead)} ${T.min}`;
      }
      if (days === 0) return clock ? `${T.today} ${clock}` : T.today;
      if (days === 1) return clock ? `${T.tomorrow} ${clock}` : T.tomorrow;
      return clock ? `${wdm}${yr} ${clock}` : `${wdm}${yr}`;
    }
    case 'header':
      if (p.monthOnly) return `${T.monLong[p.m - 1]}${yr}`;
      return `${T.wdLong[p.wd]} ${p.d} ${T.monLong[p.m - 1]}${yr}`;
    case 'notice':
      return clock ? `${wdm}${yr}, ${clock}` : `${wdm}${yr}`;
    case 'axis': {
      const year = otherYear || p.m === 1 ? ` ${p.y}` : '';
      if (opts.unit === 'day' && !p.monthOnly) return `${dm}${otherYear ? ` ${p.y}` : ''}`;
      return `${T.monShort[p.m - 1]}${year}`;
    }
    case 'audit':
      return clock ? `${wdm} ${p.y}, ${clock}` : `${wdm} ${p.y}`;
    default:
      throw new Error(`fmtDate: onbekende soort '${kind}'`);
  }
}

// Alleen de klok, 'HH:MM' in Amsterdam, voor een tijd naast een al getoonde dag
// (een berichtbubbel, een tijdslot). '' bij een kalenderdag of onleesbare invoer.
export function fmtClock(value) {
  const p = parseMoment(value);
  return p && p.hasTime ? `${p.hh}:${p.mi}` : '';
}

// Een periode: '19 t/m 23 oktober' (header) of '19 t/m 23 okt' (andere soorten).
// Jaar alleen als het afwijkt van nu of tussen begin en eind verschilt.
export function fmtRange(from, to, kind = 'header', opts = {}) {
  const a = parseMoment(from);
  const b = parseMoment(to);
  if (!a && !b) return '-';
  if (!a || !b) return fmtDate(a ? from : to, kind === 'header' ? 'header' : 'cell', opts);
  const T = langOf(opts.lang);
  const now = opts.now instanceof Date ? opts.now : (opts.now != null ? new Date(opts.now) : new Date());
  const ny = amsParts(now.getTime()).y;
  const mon = kind === 'header' ? T.monLong : T.monShort;
  const until = ` ${T.until} `;
  if (a.y === b.y) {
    const yr = a.y !== ny ? ` ${a.y}` : '';
    if (a.m === b.m) {
      if (a.d === b.d) return `${a.d} ${mon[a.m - 1]}${yr}`;
      return `${a.d}${until}${b.d} ${mon[a.m - 1]}${yr}`;
    }
    return `${a.d} ${mon[a.m - 1]}${until}${b.d} ${mon[b.m - 1]}${yr}`;
  }
  return `${a.d} ${mon[a.m - 1]} ${a.y}${until}${b.d} ${mon[b.m - 1]} ${b.y}`;
}

// ISO-waarde voor <time dateTime>: kalenderdag blijft 'YYYY-MM-DD'.
export function dateTimeAttr(value) {
  const p = parseMoment(value);
  if (!p) return undefined;
  if (p.monthOnly) return `${p.y}-${pad(p.m)}`;
  if (!p.hasTime) return keyOf(p);
  return new Date(p.ms).toISOString();
}

// Ruwe ISO-tijdstempels in tekst die voor een mens is opgeslagen (meldingen,
// berichten) worden een notice-datum. Een losse 'YYYY-MM-DD' blijft staan: die
// kan een sleutel of een bewust geschreven datum zijn.
export const ISO_TIMESTAMP_RE = /\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?/g;
export function rewriteIsoTimestamps(text, opts = {}) {
  if (typeof text !== 'string' || !text) return text;
  return text.replace(ISO_TIMESTAMP_RE, (iso) => {
    const out = fmtDate(iso, 'notice', opts);
    return out === '-' ? iso : out;
  });
}
